import {
  BookOpen,
  Gift,
  Globe,
  Instagram,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react'
import type { PosterConfig, PosterPurpose } from '@/types'

// One template per purpose: the lucide icon shown in the purpose
// switcher, the human label, and the default PosterConfig that's
// installed by migration 029. These defaults are duplicated here
// (matching the Go-side seed) so a tenant that never opened Poster
// Studio still gets a sensible preview the first time they do.
export interface PosterTemplate {
  purpose: PosterPurpose
  label: string
  icon: LucideIcon
  description: string
  defaults: PosterConfig
}

export const POSTER_TEMPLATES: PosterTemplate[] = [
  {
    purpose: 'loyalty',
    label: 'Loyalty program',
    icon: Gift,
    description: 'Sign-up poster for your punch-card loyalty programme.',
    defaults: {
      themeId: 'warm-saffron',
      layout: 'a4',
      logoSize: 'lg',
      accentOverride: null,
      headline: 'Visit 5 times. The 5th is on us.',
      subhead: 'Earn a free reward on your next loyalty visit.',
      footer: 'Scan to join — no app required.',
      qrCaption: 'Scan with your phone camera',
      showExplainer: true,
      qrTargetUrl: '',
    },
  },
  {
    purpose: 'menu',
    label: 'Menu',
    icon: BookOpen,
    description: 'Direct customers to your digital menu.',
    defaults: {
      themeId: 'cream-minimal',
      layout: 'a4',
      logoSize: 'lg',
      accentOverride: null,
      headline: 'Tonight’s menu.',
      subhead: 'Browse the full menu and order with one tap.',
      footer: 'Updated daily.',
      qrCaption: 'Scan to see the menu',
      showExplainer: false,
      qrTargetUrl: '',
    },
  },
  {
    purpose: 'reviews',
    label: 'Reviews',
    icon: Star,
    description: 'Catch happy customers right after dessert.',
    defaults: {
      themeId: 'warm-saffron',
      layout: 'tent',
      logoSize: 'md',
      accentOverride: null,
      headline: 'Loved your visit?',
      subhead: 'Tell us in 30 seconds — it means everything.',
      footer: 'Thank you.',
      qrCaption: 'Leave a review',
      showExplainer: false,
      qrTargetUrl: '',
    },
  },
  {
    purpose: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    description: 'Follow your social profile.',
    defaults: {
      themeId: 'midnight-ember',
      layout: 'card',
      logoSize: 'md',
      accentOverride: null,
      headline: 'Follow along.',
      subhead: 'New dishes, behind-the-scenes, and the occasional treat.',
      footer: '',
      qrCaption: '@yourhandle',
      showExplainer: false,
      qrTargetUrl: '',
    },
  },
  {
    purpose: 'website',
    label: 'Website',
    icon: Globe,
    description: 'Send guests to your homepage or order portal.',
    defaults: {
      themeId: 'editorial-serif',
      layout: 'a4',
      logoSize: 'lg',
      accentOverride: null,
      headline: 'Order online.',
      subhead: 'Skip the queue. Pickup or delivery.',
      footer: '',
      qrCaption: 'Scan to order',
      showExplainer: false,
      qrTargetUrl: '',
    },
  },
  {
    purpose: 'custom',
    label: 'Custom',
    icon: Sparkles,
    description: 'Blank canvas — point it anywhere.',
    defaults: {
      themeId: 'terracotta-glow',
      layout: 'a4',
      logoSize: 'lg',
      accentOverride: null,
      headline: '',
      subhead: '',
      footer: '',
      qrCaption: '',
      showExplainer: false,
      qrTargetUrl: '',
    },
  },
]

export function getPosterTemplate(purpose: PosterPurpose): PosterTemplate {
  return (
    POSTER_TEMPLATES.find((t) => t.purpose === purpose) ?? POSTER_TEMPLATES[0]
  )
}

// Used to seed missing purposes when the on-disk JSONB hasn't been
// migrated (e.g. an older dev DB). Keeps the studio robust against a
// half-populated config.
export function defaultMarketingPosterConfig(): Record<PosterPurpose, PosterConfig> {
  return POSTER_TEMPLATES.reduce(
    (acc, t) => {
      acc[t.purpose] = { ...t.defaults }
      return acc
    },
    {} as Record<PosterPurpose, PosterConfig>,
  )
}
